if (!customElements.get('quick-comparison-table')) {
  customElements.define(
    'quick-comparison-table',
    class QuickComparisonTable extends HTMLElement {
      constructor() {
        super();
        this.abortController = new AbortController();

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(this.init.bind(this), { timeout: 1500 });
        } else {
          // Safari cannot observe visibility while this element is hidden.
          window.setTimeout(this.init.bind(this), 0);
        }
      }

      async init() {
        if (this.initialized) return;
        this.initialized = true;
        if (!this.dataset.firstPage) {
          this.hideComparison();
          return;
        }

        try {
          let page = this;
          // Ignore an unrelated pagination parameter on the initial product URL.
          if (Number(this.dataset.currentPage) !== 1) {
            page = await this.fetchPage(this.dataset.firstPage);
            this.innerHTML = page.innerHTML;
          }

          const parentPage = this.parentPageFrom(page.ownerDocument);
          await this.loadRemainingPages(page);

          // Exhaust the primary category before widening the comparison. A
          // matching product may exist only on a later collection page.
          if (this.productIdsFrom(this).size < 2 && parentPage) {
            this.dataset.sourceCollection = parentPage.dataset.sourceCollection;
            this.innerHTML = parentPage.innerHTML;
            await this.loadRemainingPages(parentPage);
          }

          if (this.productIdsFrom(this).size < 2) {
            this.hideComparison();
            return;
          }

          this.style.setProperty('--comparison-product-count', this.productIdsFrom(this).size);

          // A later product may be the only one with a value for a given row.
          this.querySelectorAll('table.quick-comparison-table > tbody > tr').forEach((row) => {
            row.hidden = row.dataset.compareType !== 'info' &&
              !Array.from(row.querySelectorAll('td')).some((cell) => cell.textContent.trim());
          });
          this.hidden = false;
          this.setAttribute('aria-busy', 'false');
          this.dispatchEvent(new CustomEvent('quick-comparison-table:loaded'));
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Unable to load the similar-product comparison', error);
          this.hideComparison('quick-comparison-table:error');
        }
      }

      pageUrl(value) {
        const url = new URL(value, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        return url.toString();
      }

      parentPageFrom(document) {
        const template = Array.from(document.querySelectorAll('template[data-comparison-parent-for]'))
          .find((node) => node.dataset.comparisonParentFor === this.dataset.sectionId);
        return template ? template.content.querySelector('quick-comparison-table') : null;
      }

      async loadRemainingPages(page) {
        const visited = new Set();
        let nextPage = page.dataset.nextPage;
        while (nextPage) {
          const url = this.pageUrl(nextPage);
          if (visited.has(url)) throw new Error('Repeated comparison pagination URL');
          visited.add(url);
          page = await this.fetchPage(url);
          this.appendPage(page);
          nextPage = page.dataset.nextPage;
        }
      }

      async fetchPage(value) {
        const response = await fetch(this.pageUrl(value), {
          signal: this.abortController.signal,
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error(`Comparison request failed: ${response.status}`);
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        let page = document.querySelector('quick-comparison-table');
        if (page && page.dataset.sourceCollection !== this.dataset.sourceCollection) {
          page = this.parentPageFrom(document);
        }
        if (!page || page.dataset.sourceCollection !== this.dataset.sourceCollection) {
          throw new Error('The comparison category could not be rendered');
        }
        return page;
      }

      productIdsFrom(page) {
        const row = page.querySelector('tbody tr');
        return new Set(Array.from(row ? row.querySelectorAll('td[data-product-id]') : [])
          .map((cell) => cell.dataset.productId));
      }

      appendPage(page) {
        const knownIds = this.productIdsFrom(this);
        const newIds = new Set([...this.productIdsFrom(page)].filter((id) => !knownIds.has(id)));
        const rows = new Map(Array.from(this.querySelectorAll('table.quick-comparison-table > tbody > tr'))
          .map((row) => [row.dataset.compareKey, row]));
        page.querySelectorAll('table.quick-comparison-table > tbody > tr').forEach((sourceRow) => {
          const targetRow = rows.get(sourceRow.dataset.compareKey);
          if (!targetRow) return;
          sourceRow.querySelectorAll('td[data-product-id]').forEach((cell) => {
            if (newIds.has(cell.dataset.productId)) targetRow.appendChild(cell.cloneNode(true));
          });
        });
      }

      hideComparison(eventName = 'quick-comparison-table:empty') {
        this.hidden = true;
        this.setAttribute('aria-busy', 'false');
        const section = this.closest('.shopify-section');
        if (section) section.hidden = true;
        this.dispatchEvent(new CustomEvent(eventName));
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}
